import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SnackbarMessage from "../components/common/SnackbarMessage";

const useUpdate = (
  queryKey,
  token = null,
  customSuccessMessage = "Data updated successfully",
  customErrorMessage = "Failed to update data",
) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
    title: "",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const updateMutation = useMutation({
    mutationFn: async (data) => {
      setLoading(true);
      try {
        const headers = token ? { Authorization: `Token ${token.trim()}` } : {};
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/${queryKey}/`,
          data,
          { headers },
        );
        setResponseData(response.data);
        return response.data;
      } catch (error) {
        const errorMessage = error.response?.data?.detail || error.message;
        throw error.response?.data || error;
      }
    },
    onSuccess: (response) => {
      setSnackbar({
        open: true,
        message: customSuccessMessage,
        severity: "success",
        title: "Success",
      });

      if (response?.require_login) {
        setTimeout(() => {
          localStorage.removeItem("token");
          window.location.href = "/login";
        }, 1500);
      }

      queryClient.invalidateQueries(queryKey);
      setLoading(false);
    },
    onError: (error) => {
      setLoading(false);
      if (error && typeof error === "object") {
        const errorMessages = Object.entries(error)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`,
          )
          .join("\n");

        setSnackbar({
          open: true,
          message: errorMessages,
          severity: "error",
          title: "Error",
        });
      } else {
        setSnackbar({
          open: true,
          message: customErrorMessage,
          severity: "error",
          title: "Error",
        });
      }
    },
  });

  const handleAdd = (data) => {
    return new Promise((resolve, reject) => {
      updateMutation.mutate(data, {
        onSuccess: (response) => {
          resolve(response);
        },
        onError: (error) => {
          reject(error);
        },
      });
    });
  };

  return {
    handleAdd,
    loading: updateMutation.isLoading || loading,
    responseData,
    isError: updateMutation.isError,
    isSuccess: updateMutation.isSuccess,
    SnackbarMessage: (
      <SnackbarMessage
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
        title={snackbar.title}
      />
    ),
  };
};

export default useUpdate;
